import { UndirectedGraph } from 'graphology';

export interface Question {
  value: QuestionValues;
  label: string;
  subLabel?: string;
  isMultipleChoice: boolean;
  answers: Answer[];
  trackEventAction?: string;
  trackEventReturnLabel?: string;
}

export interface Answer {
  value: string;
  label: string;
  trackEvent: {
    action: string;
    label?: string;
    type: 'exit' | 'download' | 'action' | 'navigation';
  };
}

export enum QuestionValues {
  Accompagnement = 'accompagnement',
  Age = 'age',
  Orientation1825 = 'orientation-18-25',
  Orientation2630 = 'orientation-26-30',
  Rqth = 'rqth',
  Needs = 'needs',
  OneJOneS = '1j1s',
  LocalMission = 'localMission',
  PeSignUp = 'peSignUp',
  PeContact = 'peContact',
}

const graph = new UndirectedGraph<Question | { [name: string]: undefined }>();
const TRACK_EVENT_ACCOMPAGNEMENT_ACTION = 'Click Accompagnement';
const TRACK_EVENT_AGE_ACTION = 'Click Age';
const TRACK_EVENT_NEED_HELP_ACTION = 'Click Besoin Aide';
const TRACK_EVENT_OTHERS_NEED_ACTION = 'Click Solutions';

const questions: Question[] = [
  {
    value: QuestionValues.Accompagnement,
    label: "Bénéficiez-vous actuellement d'un accompagnement ?",
    isMultipleChoice: false,
    answers: [
      {
        value: 'localMission',
        label: 'Je suis accompagné par la Mission Locale',
        trackEvent: { action: TRACK_EVENT_ACCOMPAGNEMENT_ACTION, label: 'ML', type: 'action' },
      },
      {
        value: 'poleEmploi',
        label: 'Je suis accompagné par Pôle Emploi',
        trackEvent: { action: TRACK_EVENT_ACCOMPAGNEMENT_ACTION, label: 'PE', type: 'action' },
      },
      {
        value: 'none',
        label: 'Je ne bénéficie d’aucun accompagnement',
        trackEvent: { action: TRACK_EVENT_ACCOMPAGNEMENT_ACTION, label: 'Aucun', type: 'action' },
      },
    ],
  },
  {
    value: QuestionValues.Age,
    label: 'Quel âge avez-vous ?',
    isMultipleChoice: false,
    trackEventReturnLabel: 'Age',
    answers: [
      {
        value: '-18',
        label: 'Moins de 18 ans',
        trackEvent: { action: TRACK_EVENT_AGE_ACTION, label: '18', type: 'action' },
      },
      {
        value: '18-25',
        label: 'Entre 18 et 25 ans',
        trackEvent: { action: TRACK_EVENT_AGE_ACTION, label: '18-25', type: 'action' },
      },
      {
        value: '26-30',
        label: 'Entre 26 et 30 ans',
        trackEvent: { action: TRACK_EVENT_AGE_ACTION, label: '26-30', type: 'action' },
      },
    ],
  },
  {
    value: QuestionValues.Orientation1825,
    label:
      "Avez-vous besoin d'aide pour vous orienter, chercher un emploi, une alternance, une formation, ou travailler votre projet professionnel ?",
    isMultipleChoice: false,
    trackEventReturnLabel: 'Besoin Aide',
    answers: [
      {
        value: 'orientation-18-25-yes',
        label: 'Oui',
        trackEvent: { action: TRACK_EVENT_NEED_HELP_ACTION, label: 'Oui', type: 'action' },
      },
      {
        value: 'orientation-18-25-no',
        label: 'Non',
        trackEvent: { action: TRACK_EVENT_NEED_HELP_ACTION, label: 'Non', type: 'action' },
      },
    ],
  },
  {
    value: QuestionValues.Orientation2630,
    label:
      "Avez-vous besoin d'aide pour vous orienter, chercher un emploi, une alternance, une formation, ou travailler votre projet professionnel ?",
    isMultipleChoice: false,
    trackEventReturnLabel: 'Besoin Aide',
    answers: [
      {
        value: 'orientation-26-30-yes',
        label: 'Oui',
        trackEvent: { action: TRACK_EVENT_NEED_HELP_ACTION, label: 'Oui', type: 'action' },
      },
      {
        value: 'orientation-26-30-no',
        label: 'Non',
        trackEvent: { action: TRACK_EVENT_NEED_HELP_ACTION, label: 'Non', type: 'action' },
      },
    ],
  },
  {
    value: QuestionValues.Rqth,
    label: 'Êtes-vous en situation de handicap (RQTH) ?',
    trackEventReturnLabel: 'RQTH',
    isMultipleChoice: false,
    answers: [
      {
        value: 'rqth-yes',
        label: 'Oui',
        trackEvent: { action: 'Click RQTH', label: 'Oui', type: 'action' },
      },
      { value: 'rqth-no', label: 'Non', trackEvent: { action: 'Click RQTH', label: 'Non', type: 'action' } },
    ],
  },
  {
    value: QuestionValues.Needs,
    label: 'Rencontrez-vous d’autres besoins ?',
    trackEventReturnLabel: 'Autres Besoins',
    subLabel: 'Vous pouvez sélectionner entre 0 et 6 propositions.',
    isMultipleChoice: true,
    trackEventAction: 'Click Valider Besoins',
    answers: [
      {
        value: 'house',
        label: 'Logement',
        trackEvent: { action: TRACK_EVENT_OTHERS_NEED_ACTION, label: 'Logement', type: 'action' },
      },
      {
        value: 'health',
        label: 'Santé',
        trackEvent: { action: TRACK_EVENT_OTHERS_NEED_ACTION, label: 'Santé', type: 'action' },
      },
      {
        value: 'administrative',
        label: 'Difficultés administratives ou juridiques',
        trackEvent: { action: TRACK_EVENT_OTHERS_NEED_ACTION, label: 'Administrative', type: 'action' },
      },
      {
        value: 'rigths',
        label: "Problématique d'accès aux droits",
        trackEvent: { action: TRACK_EVENT_OTHERS_NEED_ACTION, label: 'Droits', type: 'action' },
      },
      {
        value: 'french',
        label: 'Maîtrise de français',
        trackEvent: { action: TRACK_EVENT_OTHERS_NEED_ACTION, label: 'Français', type: 'action' },
      },
      {
        value: 'family',
        label: 'Contraintes familiales',
        trackEvent: { action: TRACK_EVENT_OTHERS_NEED_ACTION, label: 'Famille', type: 'action' },
      },
    ],
  },
];

questions.forEach((question) => {
  graph.addNode(question.value, question);
});

graph.addNode(QuestionValues.OneJOneS);
graph.addNode(QuestionValues.LocalMission);
graph.addNode(QuestionValues.PeSignUp);
graph.addNode(QuestionValues.PeContact);

// accompgnement
graph.addEdgeWithKey('localMission', QuestionValues.Accompagnement, QuestionValues.LocalMission);
graph.addEdgeWithKey('poleEmploi', QuestionValues.Accompagnement, QuestionValues.PeContact);
graph.addEdgeWithKey('none', QuestionValues.Accompagnement, QuestionValues.Age);

// age
graph.addEdgeWithKey('-18', QuestionValues.Age, QuestionValues.LocalMission);
graph.addEdgeWithKey('18-25', QuestionValues.Age, QuestionValues.Orientation1825);
graph.addEdgeWithKey('26-30', QuestionValues.Age, QuestionValues.Orientation2630);

// orientation-18-25
graph.addEdgeWithKey('orientation-18-25-yes', QuestionValues.Orientation1825, QuestionValues.Needs);
graph.addEdgeWithKey('orientation-18-25-no', QuestionValues.Orientation1825, QuestionValues.OneJOneS);

// orientation-26-30
graph.addEdgeWithKey('orientation-26-30-yes', QuestionValues.Orientation2630, QuestionValues.Rqth);
graph.addEdgeWithKey('orientation-26-30-no', QuestionValues.Orientation2630, QuestionValues.OneJOneS);

// orientation-rqth
graph.addEdgeWithKey('rqth-yes', QuestionValues.Rqth, QuestionValues.Needs);
graph.addEdgeWithKey('rqth-no', QuestionValues.Rqth, QuestionValues.PeSignUp);

export const GRAPH = graph;
